import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState } from "react";
import { Search, Loader2, AlertTriangle, CheckCircle, XCircle, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface InteractionPair {
  drug1: string;
  drug2: string;
  severity: string;
  description: string;
  source: string | null;
}

interface InteractionResult {
  drugs: string[];
  interactions: InteractionPair[];
  checkedAt: string;
}

const PHARMACIST_NOTES: Record<string, { drugs: string[]; note: string; severity: "critical" | "major" | "moderate" }> = {
  "mtx-nsaid": {
    drugs: ["methotrexate", "ibuprofen", "naproxen", "diclofenac", "aspirin", "nsaid"],
    note: "NSAIDs reduce renal clearance of methotrexate, increasing toxicity risk. Monitor FBC and renal function closely. Short-term NSAID use may be acceptable at low MTX doses with renal monitoring. High-dose MTX: avoid NSAIDs.",
    severity: "major",
  },
  "mtx-trimethoprim": {
    drugs: ["methotrexate", "trimethoprim", "co-trimoxazole", "septrin"],
    note: "Life-threatening interaction. Both inhibit dihydrofolate reductase — additive antifolate effect causing severe myelosuppression. Avoid combination. Use alternative antibiotics (e.g. nitrofurantoin for UTI if appropriate).",
    severity: "critical",
  },
  "aza-allopurinol": {
    drugs: ["azathioprine", "allopurinol", "febuxostat"],
    note: "CRITICAL interaction. Xanthine oxidase inhibitors (allopurinol, febuxostat) block azathioprine metabolism, causing life-threatening myelosuppression. If combination unavoidable, reduce azathioprine dose by 75% and monitor intensively. Consider alternative urate-lowering therapy (probenecid) or alternative immunosuppressant.",
    severity: "critical",
  },
  "leflunomide-warfarin": {
    drugs: ["leflunomide", "warfarin"],
    note: "Leflunomide (teriflunomide) inhibits CYP2C9 — increases warfarin exposure. INR can rise significantly. Monitor INR closely when starting, stopping, or dose-changing leflunomide. May need warfarin dose reduction.",
    severity: "major",
  },
  "ciclosporin-statin": {
    drugs: ["ciclosporin", "simvastatin", "atorvastatin", "rosuvastatin", "statin"],
    note: "Ciclosporin significantly increases statin exposure via CYP3A4 and OATP1B1/3 inhibition. High myopathy/rhabdomyolysis risk. Simvastatin and lovastatin are contraindicated with ciclosporin. Use pravastatin (less interaction) or fluvastatin at low doses. Rosuvastatin: cap at 5mg/day.",
    severity: "critical",
  },
  "jak-live-vaccine": {
    drugs: ["baricitinib", "tofacitinib", "upadacitinib", "filgotinib", "jak"],
    note: "Live vaccines are contraindicated with JAK inhibitors. Ensure all live vaccines (MMR, varicella, yellow fever, BCG) are given ≥4 weeks before starting JAK inhibitor therapy. VZV vaccine (Shingrix — non-live) should ideally be given before starting.",
    severity: "critical",
  },
  "hydroxychloroquine-qt": {
    drugs: ["hydroxychloroquine", "azithromycin", "clarithromycin", "domperidone", "amiodarone", "sotalol"],
    note: "Hydroxychloroquine prolongs QTc. Combination with other QT-prolonging drugs increases risk of ventricular arrhythmia (torsades de pointes). Review all QT-prolonging medications. Consider ECG at baseline if multiple QT-prolonging drugs.",
    severity: "major",
  },
};

const COMMON_RHEUM_DRUGS = [
  "Methotrexate", "Hydroxychloroquine", "Sulfasalazine", "Leflunomide", "Azathioprine",
  "Adalimumab", "Etanercept", "Infliximab", "Rituximab", "Tocilizumab", "Abatacept",
  "Baricitinib", "Tofacitinib", "Upadacitinib", "Mycophenolate", "Ciclosporin",
  "Prednisolone", "Allopurinol", "Febuxostat",
];

function getSeverityStyle(severity: string) {
  if (severity === "critical") return "bg-red-50 border-red-200 text-red-800";
  if (severity === "major") return "bg-amber-50 border-amber-200 text-amber-800";
  return "bg-yellow-50 border-yellow-200 text-yellow-800";
}

function getSeverityBadge(severity: string) {
  if (severity === "critical") return <Badge className="bg-red-100 text-red-700 border border-red-200">Critical</Badge>;
  if (severity === "major") return <Badge className="bg-amber-100 text-amber-700 border border-amber-200">Major</Badge>;
  if (severity === "moderate") return <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-200">Moderate</Badge>;
  return <Badge variant="outline">{severity}</Badge>;
}

export default function PharmacistInteractions() {
  const [drugInput, setDrugInput] = useState("");
  const [drugs, setDrugs] = useState<string[]>([]);
  const [results, setResults] = useState<InteractionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const addDrug = () => {
    const trimmed = drugInput.trim();
    if (trimmed && !drugs.includes(trimmed)) {
      setDrugs((prev) => [...prev, trimmed]);
      setDrugInput("");
    }
  };

  const removeDrug = (drug: string) => {
    setDrugs((prev) => prev.filter((d) => d !== drug));
  };

  const handleCheck = async () => {
    if (drugs.length < 2) {
      setError("Please add at least 2 drugs to check interactions.");
      return;
    }
    setLoading(true);
    setError(null);
    setResults(null);
    setSearched(true);
    try {
      const resp = await fetch(`/api/interactions?drugs=${encodeURIComponent(drugs.join(","))}`);
      if (!resp.ok) throw new Error("Failed to check interactions");
      const data = await resp.json();
      setResults(data);
    } catch {
      setError("Unable to retrieve interaction data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const drugsLower = drugs.map((d) => d.toLowerCase());
  const relevantPharmacistNotes = Object.values(PHARMACIST_NOTES).filter((note) =>
    note.drugs.some((d) => drugsLower.some((input) => input.includes(d) || d.includes(input)))
  );

  const interactions = results?.interactions ?? [];

  return (
    <PharmacistLayout title="Drug Interactions">
      <div className="space-y-6">
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 text-sm text-violet-800">
          <strong>Pharmacist-level interaction checker.</strong> Interaction data sourced from RxNorm/NLM. Pharmacist-specific clinical management notes are provided for key rheumatology interactions. Always verify with the BNF, Stockley's Drug Interactions, and local formulary. Clinical judgment applies.
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <h3 className="font-semibold text-slate-900">Add drugs to check</h3>

          <div className="flex flex-wrap gap-2 mb-2">
            {COMMON_RHEUM_DRUGS.map((drug) => (
              <button
                key={drug}
                onClick={() => {
                  if (!drugs.includes(drug)) setDrugs((prev) => [...prev, drug]);
                }}
                disabled={drugs.includes(drug)}
                className="text-xs bg-slate-100 hover:bg-violet-100 text-slate-700 hover:text-violet-800 px-2.5 py-1 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {drug}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <Input
              placeholder="Type a drug name and press Add..."
              value={drugInput}
              onChange={(e) => setDrugInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDrug(); } }}
            />
            <Button onClick={addDrug} variant="outline">Add</Button>
          </div>

          {drugs.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {drugs.map((drug) => (
                <span key={drug} className="flex items-center gap-1.5 bg-violet-100 text-violet-800 text-sm px-3 py-1 rounded-full">
                  {drug}
                  <button onClick={() => removeDrug(drug)} className="hover:text-violet-600">×</button>
                </span>
              ))}
            </div>
          )}

          <Button
            onClick={handleCheck}
            disabled={drugs.length < 2 || loading}
            className="bg-violet-600 hover:bg-violet-700 text-white"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            Check Interactions
          </Button>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        {drugs.length >= 1 && relevantPharmacistNotes.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Pharmacist Clinical Alerts
            </h3>
            {relevantPharmacistNotes.map((note, i) => (
              <div key={i} className={`rounded-xl border p-4 ${getSeverityStyle(note.severity)}`}>
                <div className="flex items-center gap-2 mb-2">
                  {getSeverityBadge(note.severity)}
                  <span className="text-xs font-medium">Pharmacist-curated interaction note</span>
                </div>
                <p className="text-sm leading-relaxed">{note.note}</p>
              </div>
            ))}
          </div>
        )}

        {searched && !loading && (
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              RxNorm Interaction Data
            </h3>
            {interactions.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                <CheckCircle className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-slate-700 font-medium">No known interactions found in RxNorm database</p>
                <p className="text-sm text-slate-500 mt-1">This does not exclude all interactions — always verify with BNF and Stockley's.</p>
              </div>
            ) : (
              interactions.map((pair, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-start gap-2 mb-1">
                    <XCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <span className="text-sm font-medium text-slate-900">{pair.drug1} + {pair.drug2}</span>
                    </div>
                  </div>
                  {pair.severity && <p className="text-xs text-slate-500 mb-1">Severity: {pair.severity}</p>}
                  <p className="text-sm text-slate-700 leading-relaxed">{pair.description}</p>
                  {pair.source && <p className="text-xs text-slate-400 mt-1">Source: {pair.source}</p>}
                </div>
              ))
            )}
            <p className="text-xs text-slate-400">
              Interaction data from RxNorm/NLM. For comprehensive UK interaction guidance, use the BNF Interactions section or Stockley's Drug Interactions. Yellow Card any adverse events.
            </p>
          </div>
        )}
      </div>
    </PharmacistLayout>
  );
}
